import { Link, NavLink, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { LOGO_URL } from "@/lib/config";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/tours", label: "Safaris" },
  { to: "/custom-trip", label: "Plan Trip" },
  { to: "/gallery", label: "Gallery" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      data-testid="site-header"
      className="fixed top-4 md:top-6 left-0 right-0 z-40 px-4 md:px-8"
    >
      <div
        className={`max-w-6xl mx-auto rounded-full transition-all duration-300 ${
          scrolled
            ? "bg-[#FAF6F0]/95 backdrop-blur shadow-lg shadow-[#23170E]/8"
            : "bg-[#FAF6F0]/85 backdrop-blur-sm shadow-md shadow-[#23170E]/5"
        }`}
      >
        <div className="flex items-center justify-between pl-4 md:pl-6 pr-2 md:pr-2 h-16">
          <Link to="/" data-testid="logo-home-link" className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-full overflow-hidden bg-white ring-1 ring-[#23170E]/10 flex-shrink-0 shadow-sm">
              <img
                src={LOGO_URL}
                alt="Apex Safaris — Experience the Spirit of Africa"
                className="w-full h-full object-contain"
                style={{ transform: "scale(1.35)" }}
              />
            </div>
            <span className="font-marcellus text-xl sm:text-2xl text-[#23170E] tracking-tight leading-none whitespace-nowrap">Apex Safaris</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {navItems.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/"}
                data-testid={`nav-${it.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium ${isActive ? "text-[#A6521B]" : "text-[#23170E]"} hover:text-[#A6521B] transition-colors`
                }
              >
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/tours"
              data-testid="header-book-cta"
              className="hidden md:inline-flex btn-pill btn-pill-primary whitespace-nowrap"
            >
              Booking Now
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              className="lg:hidden p-2 mr-1"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden max-w-6xl mx-auto mt-2 bg-[#FAF6F0] rounded-3xl border border-[#E2D5C3] shadow-lg shadow-[#23170E]/8">
          <div className="px-6 py-6 flex flex-col gap-5">
            {navItems.map((it) => (
              <Link
                key={it.to}
                to={it.to}
                data-testid={`mobile-nav-${it.label.toLowerCase()}`}
                className="text-[#23170E] text-base"
              >
                {it.label}
              </Link>
            ))}
            <Link
              to="/tours"
              data-testid="mobile-book-cta"
              className="btn-pill btn-pill-primary justify-center"
            >
              Booking Now
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
